export interface MentionCandidate {
  id: string;
  username: string;
  email?: string;
}

export interface MentionQueryMatch {
  query: string;
  start: number;
  end: number;
}

const PLAIN_MENTION_RE = /(^|[\s([{\n])@([A-Za-z0-9._-]+)/g;
const ACTIVE_MENTION_QUERY_RE = /(?:^|[\s([{\n])@([A-Za-z0-9._-]*)$/;
const MARKDOWN_MENTION_RE = /\[[^\]]+\]\(mention:([^)]+)\)/g;

export function getPlainMentionSpans(value: string, candidates: MentionCandidate[] = []) {
  const usernames = new Set(candidates.map(candidate => candidate.username.trim().toLowerCase()));
  return Array.from(value.matchAll(PLAIN_MENTION_RE), match => {
    let username = match[2];
    // Prefer an exact account name, including legal dots. Only interpret
    // trailing dots as punctuation when the shorter account actually exists.
    const withoutTrailingDots = username.replace(/\.+$/, '');
    if (!usernames.has(username.toLowerCase()) && usernames.has(withoutTrailingDots.toLowerCase())) {
      username = withoutTrailingDots;
    }
    const start = (match.index ?? 0) + match[1].length;
    return { username, start, end: start + username.length + 1 };
  });
}

export function getActiveMentionQuery(value: string, cursorIndex: number): MentionQueryMatch | null {
  const safeCursorIndex = Math.max(0, Math.min(cursorIndex, value.length));
  const prefix = value.slice(0, safeCursorIndex);
  const match = prefix.match(ACTIVE_MENTION_QUERY_RE);
  if (!match || typeof match.index !== 'number') {
    return null;
  }

  const query = match[1] ?? '';
  return {
    query,
    start: safeCursorIndex - query.length - 1,
    end: safeCursorIndex,
  };
}

export function insertMentionAtRange(
  value: string,
  match: MentionQueryMatch,
  username: string
): { value: string; cursorIndex: number } {
  const replacement = `@${username} `;
  const nextValue = `${value.slice(0, match.start)}${replacement}${value.slice(match.end)}`;
  return {
    value: nextValue,
    cursorIndex: match.start + replacement.length,
  };
}

export function resolveMentionIdsFromText(
  value: string,
  candidates: MentionCandidate[]
): string[] {
  const byUsername = new Map<string, MentionCandidate>();
  candidates.forEach((candidate) => {
    const key = candidate.username.trim().toLowerCase();
    if (!key) return;
    byUsername.set(key, candidate);
  });

  const resolvedIds: string[] = [];
  const seen = new Set<string>();

  for (const match of value.matchAll(MARKDOWN_MENTION_RE)) {
    const id = String(match[1] || '').trim();
    if (!id || seen.has(id)) continue;
    seen.add(id);
    resolvedIds.push(id);
  }

  for (const match of getPlainMentionSpans(value, candidates)) {
    const username = match.username.toLowerCase();
    if (!username) continue;
    const candidate = byUsername.get(username);
    if (!candidate || seen.has(candidate.id)) continue;
    seen.add(candidate.id);
    resolvedIds.push(candidate.id);
  }

  return resolvedIds;
}
